/**
 * Wrapper for logic to synchronize one viewport with another.
 */
export class ViewTracker {
  constructor (viewport) {
    this.viewport = viewport;
    this._vpState = viewport.getVPView();
    this._viewport = viewport;
    this._isSelected = false;
    this._isStarted = false; // To track the manual sync on/off status
  }

  get isSelected () { return this._isSelected; }
  set isSelected (value) {
    if (this._isSelected !== value) {
      this._isSelected = value;
      this._updateVpReadOnlyMode();
    }
  }

  /**
     * Creates reference "initial state" properties to track changes to the viewport's state.
     */
  start () {
    this._isStarted = true;
    this._updateVpReadOnlyMode();

    const initialState = Object.assign({}, this._vpState);
    this._requestedZoom = initialState.zoom;
    this._requestedPan = {
      left: initialState.left,
      top: initialState.top
    };
    // Because rotation is unbounded there's no need to track its position relative to its initial state.
  }

  stop () {
    this._isStarted = false;
    this._updateVpReadOnlyMode();

    delete this._requestedZoom;
    delete this._requestedPan;
  }

  /**
     * Update tracker VP readonly param to false so that other plugin should not overlap
     */
  _updateVpReadOnlyMode () {
    // To ensure other plugins are enabled on adding VP where manual sync is disable
    // To ensure other plugins are disabled only for tracked VP when manual sync ON
    // To ensure other plugins are re-enabled for tracked VP when manual sync OFF
    this.viewport.setReadOnlyMode(!this.isSelected && this._isStarted);
  }

  /**
     * Updates the tracking properties and, if required, updates the viewport itself.
     * @param deltaState
     */
  updateState (deltaState) {
    const viewport = this.viewport;

    // Note the "else" statements. The reason for these is that zoom and rotate affect the bounds of the viewport, making
    // it appear as if it has panned when really it hasn't. If we allowed fallthrough zoom and rotate operations
    // behave weirdly. The "else" fixes this, but has the potential to introduce errors if the user actually zooms
    // then pans between repaints. At 30 fps this is unlikely but if needed we can probably fix by keeping a more
    // accurate delta in the state monitor.

    // Zoom only if needed.
    if (deltaState.zoomRatio !== 1 && Number.isFinite(deltaState.zoomRatio)) {
      this._updateRequestedZoom(deltaState.zoomRatio);
      // Zooming alters the coordinate system, so also sync the x,y coordinates.
      this._updateRequestedPan(deltaState.left, deltaState.top, this._viewport.getNormalizationFactor());

      this._zoomTheViewport();
    } else if (!this.isSelected && deltaState.angleDeg) {
      // Rotate only if needed.
      viewport.viewRotate(this._vpState.angleDeg + deltaState.angleDeg);
    } else if (deltaState.left || deltaState.top) {
      // Pan only if needed.
      this._updateRequestedPan(deltaState.left, deltaState.top, this._viewport.getNormalizationFactor());
      this._moveTheViewport();
    }
  }

  /**
     * Solely here to handle the case when the unpinned image is closed. Exposes the tracker's initial state
     * so that it can be restored. See the description for _isClosingUnpinnedImage in ManualSyncService for more info.
     * @returns {{zoom: *, pan: *}}
     */
  getState () {
    const {top, left, angleDeg, zoom} = this._vpState;
    return {
      trackerState: {
        zoom: this._requestedZoom,
        pan: this._requestedPan
      },
      viewportState: {top, left, angleDeg, zoom}
    };
  }

  /**
     * Solely here to handle the case when the unpinned image is closed. Restores the tracker's initial state.
     * See the description for _isClosingUnpinnedImage in ManualSyncService for more info.
     * @param trackerState
     * @param viewportState
     */
  setState ({trackerState, viewportState}) {
    this._requestedZoom = trackerState.zoom;
    this._requestedPan = trackerState.pan;

    this.viewport.zoomAbsolute(viewportState.zoom);
    this.viewport.moveAbsolute(viewportState.left, viewportState.top);
    this.viewport.viewRotate(viewportState.angleDeg);
  }

  /**
     * Updates requested zoom level.
     * @param deltaZoom - the amount to change zoom.
     * @private
     */
  _updateRequestedZoom (deltaZoom) {
    this._requestedZoom *= deltaZoom; // update the tracked VP w.r.t selected VP ratio
  }

  /**
     * Update's the viewport's zoom to the requested zoom level.
     * @private
     */
  _zoomTheViewport () {
    if (this.isSelected) {
      return;
    }
    this.viewport.zoomAbsolute(this._requestedZoom);
  }

  /**
     * Updates this._requestedPan by the deltaTop and Left
     * @param deltaLeft amount to pan left
     * @param deltaTop amount to pan top
     * @param normalizationFactor scale the pan operation, if needed.
     * @private
     */
  _updateRequestedPan (deltaLeft, deltaTop, normalizationFactor) {
    this._requestedPan = {
      left: (this._requestedPan.left + deltaLeft) * normalizationFactor,
      top: (this._requestedPan.top + deltaTop) * normalizationFactor
    };
  }

  /**
     * Commands the viewport to pan the viewport by this._requestedPan.
     * @private
     */
  _moveTheViewport () {
    if (this.isSelected) {
      return;
    }
    this.viewport.moveAbsolute(this._requestedPan.left, this._requestedPan.top);
  }
}

export const trackerFactory = {
  create (viewer) {
    return new ViewTracker(viewer);
  }
};



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/manual-sync/ViewTracker.js